"use strict";
/* Copyright (c) 2018~2999 - Cologler <skyoflw@gmail.com> */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ioc = exports.ServiceProvider = exports.Resolvers = exports.LifeTime = exports.Symbols = void 0;
exports.Symbols = {
    Provider: Symbol('Provider'),
    RootProvider: Symbol('RootProvider'),
    Cache: Symbol('Cache'),
    MissingResolver: Symbol('MissingResolver'),
};
var LifeTime;
(function (LifeTime) {
    LifeTime[LifeTime["Transient"] = 0] = "Transient";
    LifeTime[LifeTime["Scoped"] = 1] = "Scoped";
    LifeTime[LifeTime["Singleton"] = 2] = "Singleton";
})(LifeTime = exports.LifeTime || (exports.LifeTime = {}));
var Services;
(function (Services) {
    class ServiceInfo {
        constructor(_factory, _lifetime, _provider) {
            this._factory = _factory;
            this._lifetime = _lifetime;
            this._provider = _provider;
            this._cache_value = null;
        }
        get(provider) {
            switch (this._lifetime) {
                case LifeTime.Transient:
                    return this._factory(provider);
                case LifeTime.Scoped:
                    const cache = provider.get(exports.Symbols.Cache);
                    if (!cache.has(this)) {
                        cache.set(this, this._factory(provider));
                    }
                    return cache.get(this);
                case LifeTime.Singleton:
                    if (this._cache_value === null) {
                        this._cache_value = [this._factory(this._provider)];
                    }
                    return this._cache_value[0];
            }
        }
    }
    Services.ServiceInfo = ServiceInfo;
    class ProviderServiceInfo {
        get(provider) {
            return provider;
        }
    }
    Services.ProviderServiceInfo = ProviderServiceInfo;
    class ValueServiceInfo {
        constructor(_value) {
            this._value = _value;
        }
        get(provider) {
            return this._value;
        }
    }
    Services.ValueServiceInfo = ValueServiceInfo;
    class GroupedServiceInfo {
        constructor(_keys) {
            this._keys = _keys;
        }
        get(provider) {
            return this._keys.map(key => provider.get(key));
        }
    }
    Services.GroupedServiceInfo = GroupedServiceInfo;
    class BindedServiceInfo {
        constructor(_targetKey) {
            this._targetKey = _targetKey;
        }
        get(provider) {
            return provider.get(this._targetKey);
        }
    }
    Services.BindedServiceInfo = BindedServiceInfo;
})(Services || (Services = {}));
var Resolvers;
(function (Resolvers) {
    class EmptyServiceInfoResolver {
        get(provider, key) {
            return undefined;
        }
    }
    Resolvers.EmptyServiceInfoResolver = EmptyServiceInfoResolver;
})(Resolvers = exports.Resolvers || (exports.Resolvers = {}));
var Utils;
(function (Utils) {
    // value of ChainMap is IServiceInfo, it cannot be undefined.
    class ChainMap {
        constructor(parentMaps = null) {
            this._maps = [new Map()];
            this._maps.push(...parentMaps || []);
        }
        get(key) {
            for (const map of this._maps) {
                const list = map.get(key);
                if (list !== undefined) {
                    return list[list.length - 1];
                }
            }
            return undefined;
        }
        getMany(key) {
            const items = [];
            for (const map of this._maps) {
                const list = map.get(key);
                if (list !== undefined) {
                    items.push(...list.slice().reverse());
                }
            }
            return items;
        }
        set(key, value) {
            const map = this._maps[0];
            let list = map.get(key);
            if (list === undefined) {
                list = [];
                map.set(key, list);
            }
            list.push(value);
        }
        child() {
            return new ChainMap(this._maps);
        }
    }
    Utils.ChainMap = ChainMap;
    class ResolveStack {
        constructor() {
            this._resolveStack = new Set();
        }
        with(key, resolver) {
            if (this._resolveStack.has(key)) {
                const chain = [...this._resolveStack, key].join(' => ');
                throw new Error(`Recursive detected. Chain: ${chain}`);
            }
            this._resolveStack.add(key);
            try {
                return resolver();
            }
            finally {
                this._resolveStack.delete(key);
            }
        }
    }
    Utils.ResolveStack = ResolveStack;
})(Utils || (Utils = {}));
class ScopedServiceProvider {
    constructor(_services) {
        this._services = _services;
        this._resolveStack = new Utils.ResolveStack();
        this.registerValue(exports.Symbols.Cache, new Map());
    }
    _getServiceInfo(key) {
        let serviceInfo = this._services.get(key);
        if (serviceInfo === undefined) {
            const resolverServiceInfo = this._services.get(exports.Symbols.MissingResolver);
            const resolver = resolverServiceInfo.get(this);
            serviceInfo = resolver.get(this, key);
        }
        return serviceInfo;
    }
    _getServiceInfos(key) {
        return this._services.getMany(key);
    }
    get(key) {
        const serviceInfo = this._getServiceInfo(key);
        if (serviceInfo) {
            return this._resolveStack.with(key, () => serviceInfo.get(this));
        }
        return undefined;
    }
    getRequired(key) {
        const value = this.get(key);
        if (value === undefined) {
            throw new Error('unable to resolve the service');
        }
        return value;
    }
    getMany(key) {
        const serviceInfos = this._getServiceInfos(key);
        return this._resolveStack.with(key, () => serviceInfos.map(si => si.get(this)));
        ;
    }
    registerServiceInfo(key, serviceInfo) {
        this._services.set(key, serviceInfo);
    }
    registerValue(key, value) {
        this._services.set(key, new Services.ValueServiceInfo(value));
    }
    register(key, factory, lifetime) {
        this._services.set(key, new Services.ServiceInfo(factory, lifetime, this));
    }
    registerTransient(key, factory) {
        return this.register(key, factory, LifeTime.Transient);
    }
    registerScoped(key, factory) {
        return this.register(key, factory, LifeTime.Scoped);
    }
    registerSingleton(key, factory) {
        return this.register(key, factory, LifeTime.Singleton);
    }
    registerGroup(key, keys) {
        this._services.set(key, new Services.GroupedServiceInfo(keys));
    }
    registerBind(key, target) {
        this._services.set(key, new Services.BindedServiceInfo(target));
    }
    scope() {
        return new ScopedServiceProvider(this._services.child());
    }
}
/**
 * the main class for anyioc.
 *
 * @export
 * @class ServiceProvider
 * @extends {ScopedServiceProvider}
 */
class ServiceProvider extends ScopedServiceProvider {
    constructor() {
        super(new Utils.ChainMap());
        this.registerServiceInfo(exports.Symbols.Provider, new Services.ProviderServiceInfo());
        this.registerValue(exports.Symbols.RootProvider, this);
        this.registerValue(exports.Symbols.MissingResolver, new Resolvers.EmptyServiceInfoResolver());
        // alias
        this.registerTransient('ioc', ioc => ioc.get(exports.Symbols.Provider));
        this.registerTransient('provider', ioc => ioc.get(exports.Symbols.Provider));
    }
}
exports.ServiceProvider = ServiceProvider;
// gist: a85a89bcdc9445148ce213a3d31eeeb2
function getGlobal() {
    if (typeof globalThis !== 'undefined') {
        return globalThis;
    }
    else if (typeof window !== 'undefined') {
        return window; // browser
    }
    else if (typeof global != 'undefined') {
        return global; // node
    }
    else {
        throw Error('unknown');
    }
}
const iocSymbol = Symbol.for('anyioc://ioc');
exports.ioc = (function () {
    const g = getGlobal();
    if (g[iocSymbol] === undefined) {
        g[iocSymbol] = new ServiceProvider();
    }
    return g[iocSymbol];
})();
//# sourceMappingURL=anyioc.js.map